// Legal documents (HR + EN). Company details supplied by the operator.
export const COMPANY = {
  legalName: "Juke Transfers j.d.o.o.",
  director: "Ivan Jukica",
  oib: "05308456115",
  mbs: "060515900",
  activityCode: "H49330",
  incorporated: "29.05.2026",
  bank: "ERSTE & STEIERM\u00c4RKISCHE BANK d.d.",
  iban: "HR8124020061101346755",
  support: "support@ledger-lens.app",
  sender: "noreply@ledger-lens.app",
  domain: "ledger-lens.app",
  jurisdiction: { hr: "Republika Hrvatska", en: "Republic of Croatia" },
};

export const LAST_UPDATED = "2026-07-29";

const identityBlock = (lang) =>
  lang === "hr"
    ? [
        `Pružatelj usluge: **${COMPANY.legalName}** ("LedgerLens", "mi")`,
        `Direktor i zakonski zastupnik: ${COMPANY.director}`,
        `OIB: ${COMPANY.oib} · MBS: ${COMPANY.mbs} · Šifra djelatnosti: ${COMPANY.activityCode}`,
        `Datum osnivanja: ${COMPANY.incorporated}`,
        `Banka: ${COMPANY.bank} · IBAN: ${COMPANY.iban}`,
        `Kontakt: ${COMPANY.support} · ${COMPANY.domain}`,
      ]
    : [
        `Service provider: **${COMPANY.legalName}** ("LedgerLens", "we")`,
        `Director and legal representative: ${COMPANY.director}`,
        `OIB (tax ID): ${COMPANY.oib} · Company reg. no. (MBS): ${COMPANY.mbs} · Activity code: ${COMPANY.activityCode}`,
        `Date of incorporation: ${COMPANY.incorporated}`,
        `Bank: ${COMPANY.bank} · IBAN: ${COMPANY.iban}`,
        `Contact: ${COMPANY.support} · ${COMPANY.domain}`,
      ];

export const LEGAL = {
  terms: {
    hr: {
      title: "Uvjeti korištenja",
      intro:
        "Ovi Uvjeti korištenja uređuju korištenje LedgerLens platforme za skeniranje računa, evidenciju troškova, obračun PDV-a i naknada zaposlenicima. Korištenjem usluge prihvaćate ove uvjete.",
      sections: [
        { h: "1. Podaci o pružatelju usluge", list: identityBlock("hr") },
        {
          h: "2. Opis usluge",
          p: "LedgerLens je softver kao usluga (SaaS) koji omogućuje: učitavanje i automatsko čitanje (OCR) poslovnih računa, izračun osnovice i PDV-a, evidenciju troškova po zaposlenicima, izračun naknada prema pravilima koje određuje korisnik te izradu izvještaja u PDF i CSV formatu.",
        },
        {
          h: "3. Račun korisnika i odgovornosti",
          list: [
            "Korisnik je poslovni subjekt koji otvara račun društva i odgovoran je za sve korisnike koje pozove.",
            "Korisnik je odgovoran za čuvanje pristupnih podataka i za sadržaj koji unosi u sustav.",
            "Zabranjeno je učitavanje sadržaja koji nije poslovni račun ili dokument povezan s troškom.",
          ],
        },
        {
          h: "4. Automatsko čitanje podataka i točnost",
          p: "OCR i modeli umjetne inteligencije koriste se kao pomoć pri unosu podataka. Iako je točnost visoka, moguće su greške u čitanju. Korisnik je dužan pregledati i potvrditi podatke prije korištenja u knjigovodstvene i porezne svrhe. LedgerLens ne pruža knjigovodstveno ni pravno savjetovanje i ne odgovara za porezne obveze korisnika.",
        },
        {
          h: "5. Pretplata, cijene i naplaćivanje",
          list: [
            "Usluga se naplaćuje mjesečno prema odabranom paketu: osnovna naknada uvećana za cijenu po aktivnom korisniku (mjestu).",
            "Svaki paket uključuje određeni broj skeniranja mjesečno; skeniranja iznad uključenog broja naplaćuju se po jedinici, ako je društvo uključilo naplaćivanje prekoračenja.",
            "Naplata se provodi putem pružatelja platnih usluga Stripe. Cijene su izražene u eurima (EUR).",
            "Probni period traje 14 dana s ograničenim brojem skeniranja i ne zahtijeva karticu.",
          ],
        },
        {
          h: "6. Dostupnost usluge",
          p: "Usluga se pruža po principu „kakva jest“ uz razumne napore za visoku dostupnost. Planirani prekidi zbog održavanja najavljuju se kad je to izvedivo.",
        },
        {
          h: "7. Zaštita podataka",
          p: "Obrada osobnih podataka opisana je u Politici privatnosti. Podaci svakog društva strogo su odvojeni identifikatorom društva (tenant ID) i nisu dostupni drugim korisnicima platforme.",
        },
        {
          h: "8. Ograničenje odgovornosti",
          p: "Odgovornost pružatelja usluge ograničena je na iznos pretplate plaćen u zadnjih tri (3) mjeseca. Pružatelj ne odgovara za posrednu štetu, izgubljenu dobit ni za posljedice pogrešnih poreznih prijava temeljenih na nepregledanim podacima.",
        },
        {
          h: "9. Prekid ugovora",
          p: "Pretplatu možete otkazati u svakom trenutku; otkaz stupa na snagu na kraju tekućeg obračunskog razdoblja. Plaćene mjesečne naknade ne vraćaju se proporcionalno (vidi Politiku povrata).",
        },
        {
          h: "10. Primjenjivo pravo",
          p: `Na ove uvjete primjenjuje se pravo ${COMPANY.jurisdiction.hr}. Za sve sporove nadležan je stvarno nadležni sud u Republici Hrvatskoj.`,
        },
      ],
    },
    en: {
      title: "Terms of Service",
      intro:
        "These Terms of Service govern the use of the LedgerLens platform for receipt scanning, expense tracking, VAT calculation and employee refunds. By using the service you accept these terms.",
      sections: [
        { h: "1. Service provider", list: identityBlock("en") },
        {
          h: "2. Description of the service",
          p: "LedgerLens is software as a service (SaaS) that allows you to upload and automatically read (OCR) business receipts, calculate the net base and VAT, track expenses per employee, calculate refunds according to rules you define, and generate PDF and CSV reports.",
        },
        {
          h: "3. Accounts and responsibilities",
          list: [
            "The customer is a business entity that creates a company account and is responsible for all users it invites.",
            "The customer is responsible for safeguarding credentials and for the content it uploads.",
            "Uploading content that is not a business receipt or an expense-related document is not permitted.",
          ],
        },
        {
          h: "4. Automated data extraction and accuracy",
          p: "OCR and AI models assist with data entry. Although accuracy is high, extraction errors are possible. The customer must review and confirm the data before using it for accounting or tax purposes. LedgerLens does not provide accounting or legal advice and is not responsible for the customer's tax obligations.",
        },
        {
          h: "5. Subscription, pricing and billing",
          list: [
            "The service is billed monthly according to the selected plan: a platform fee plus a price per active user (seat).",
            "Each plan includes a monthly scan volume; scans above the included volume are billed per scan when the company has enabled billed overage.",
            "Payments are processed by Stripe. Prices are stated in euro (EUR).",
            "The trial period lasts 14 days with a limited number of scans and requires no card.",
          ],
        },
        {
          h: "6. Availability",
          p: 'The service is provided "as is" with reasonable efforts towards high availability. Planned maintenance windows are announced where feasible.',
        },
        {
          h: "7. Data protection",
          p: "Processing of personal data is described in the Privacy Policy. Each company's data is strictly separated by a tenant identifier and is not accessible to other customers of the platform.",
        },
        {
          h: "8. Limitation of liability",
          p: "Liability is limited to the subscription fees paid in the last three (3) months. We are not liable for indirect damages, lost profit, or the consequences of incorrect tax filings based on unreviewed data.",
        },
        {
          h: "9. Termination",
          p: "You may cancel at any time; cancellation takes effect at the end of the current billing period. Monthly fees already paid are not refunded pro rata (see the Refund Policy).",
        },
        {
          h: "10. Governing law",
          p: `These terms are governed by the laws of the ${COMPANY.jurisdiction.en}. Croatian courts have jurisdiction over any disputes.`,
        },
      ],
    },
  },

  privacy: {
    hr: {
      title: "Politika privatnosti",
      intro:
        "Ova Politika privatnosti objašnjava kako LedgerLens obrađuje osobne podatke u skladu s Općom uredbom o zaštiti podataka (GDPR) i hrvatskim propisima.",
      sections: [
        { h: "1. Voditelj obrade", list: identityBlock("hr") },
        {
          h: "2. Koje podatke obrađujemo",
          list: [
            "Podaci računa: ime i prezime, poslovna e-pošta, rola, lozinka u kriptiranom obliku (bcrypt).",
            "Podaci društva: naziv, OIB, adresa, valuta, jezik, postavke PDV-a i naknada.",
            "Sadržaj računa: fotografija računa i podaci pročitani s nje (izdavatelj, broj, datum, iznosi, PDV, kategorija).",
            "Tehnički podaci: zapisi o prijavama, radnjama u sustavu (revizijski zapis) i poslanim e-porukama.",
            "Podaci o naplati: identifikatori pretplate i transakcija kod Stripe-a (ne čuvamo podatke o kartici).",
          ],
        },
        {
          h: "3. Svrhe i pravna osnova",
          list: [
            "Izvršenje ugovora (čl. 6/1/b GDPR): pružanje usluge, obrada računa, izvještaji, naplata.",
            "Legitimni interes (čl. 6/1/f): sigurnost sustava, otkrivanje duplikata i prijevara, poboljšanje usluge.",
            "Zakonska obveza (čl. 6/1/c): čuvanje računovodstvene dokumentacije.",
          ],
        },
        {
          h: "4. Primatelji i obrada od strane trećih strana",
          list: [
            "Google (Gemini API) — automatsko čitanje sadržaja fotografije računa. Slike se obrađuju radi izdvajanja podataka i ne koriste se za treniranje modela.",
            "SendGrid (Twilio) — dostava transakcijskih e-poruka (pozivi, obavijesti, izvještaji).",
            "Stripe — obrada plaćanja i pretplata.",
            "Pružatelj cloud infrastrukture na kojoj je aplikacija poslužena.",
          ],
        },
        {
          h: "5. Međunarodni prijenosi",
          p: "Kad se podaci prenose izvan EU/EGP-a, prijenos se temelji na standardnim ugovornim klauzulama Europske komisije ili odluci o primjerenosti.",
        },
        {
          h: "6. Rok čuvanja",
          list: [
            "Podaci računa i računi (dokumenti) čuvaju se do brisanja računa društva ili do isteka zakonskih rokova čuvanja knjigovodstvene dokumentacije.",
            "Revizijski zapisi i dnevnik e-pošte: do 24 mjeseca.",
            "Nakon prekida pretplate podaci se brišu ili anonimiziraju na zahtjev, uz zadržavanje onoga što zakon nalaže.",
          ],
        },
        {
          h: "7. Vaša prava",
          p: `Imate pravo na pristup, ispravak, brisanje, ograničenje obrade, prenosivost podataka i prigovor. Zahtjev pošaljite na ${COMPANY.support}. Pritužbu možete podnijeti Agenciji za zaštitu osobnih podataka (AZOP).`,
        },
        {
          h: "8. Sigurnost",
          list: [
            "Stroga izolacija podataka po društvu (tenant ID na svakom zapisu i u svakom upitu).",
            "Lozinke se čuvaju isključivo kao bcrypt hash.",
            "Pristup po rolama (administrator, zaposlenik, knjigovođa — samo pregled).",
            "Prijenos podataka zaštićen je TLS-om.",
          ],
        },
        {
          h: "9. Kolačići i lokalno spremanje",
          p: "Koristimo isključivo tehnički nužno lokalno spremanje u pregledniku (token prijave, odabrani jezik i tema). Ne koristimo marketinške ni praćenje trećih strana.",
        },
      ],
    },
    en: {
      title: "Privacy Policy",
      intro:
        "This Privacy Policy explains how LedgerLens processes personal data in accordance with the General Data Protection Regulation (GDPR) and Croatian law.",
      sections: [
        { h: "1. Data controller", list: identityBlock("en") },
        {
          h: "2. Data we process",
          list: [
            "Account data: name, work email, role, password stored as a bcrypt hash.",
            "Company data: name, tax ID, address, currency, language, VAT and refund settings.",
            "Receipt content: the receipt photo and the data extracted from it (vendor, number, date, amounts, VAT, category).",
            "Technical data: sign-in records, in-app action logs (audit trail) and a log of emails sent.",
            "Billing data: Stripe subscription and transaction identifiers (we never store card data).",
          ],
        },
        {
          h: "3. Purposes and legal bases",
          list: [
            "Performance of a contract (Art. 6(1)(b)): providing the service, processing receipts, reports, billing.",
            "Legitimate interest (Art. 6(1)(f)): system security, duplicate and fraud detection, service improvement.",
            "Legal obligation (Art. 6(1)(c)): retention of accounting documentation.",
          ],
        },
        {
          h: "4. Recipients and third-party processing",
          list: [
            "Google (Gemini API) — automated extraction of data from receipt images. Images are processed to extract data and are not used to train models.",
            "SendGrid (Twilio) — delivery of transactional email (invites, alerts, reports).",
            "Stripe — payment and subscription processing.",
            "The cloud infrastructure provider hosting the application.",
          ],
        },
        {
          h: "5. International transfers",
          p: "Where data is transferred outside the EU/EEA, the transfer relies on the European Commission's Standard Contractual Clauses or an adequacy decision.",
        },
        {
          h: "6. Retention",
          list: [
            "Account data and receipts are kept until the company account is deleted or statutory accounting retention periods expire.",
            "Audit trail and email log: up to 24 months.",
            "After cancellation, data is deleted or anonymised on request, except where the law requires retention.",
          ],
        },
        {
          h: "7. Your rights",
          p: `You have the right of access, rectification, erasure, restriction, data portability and objection. Send requests to ${COMPANY.support}. You may lodge a complaint with the Croatian Personal Data Protection Agency (AZOP).`,
        },
        {
          h: "8. Security",
          list: [
            "Strict per-company data isolation (tenant ID on every record and in every query).",
            "Passwords are stored only as bcrypt hashes.",
            "Role-based access (administrator, employee, accountant — read-only).",
            "Data in transit is protected with TLS.",
          ],
        },
        {
          h: "9. Cookies and local storage",
          p: "We use strictly necessary browser local storage only (session token, chosen language and theme). We do not use marketing or third-party tracking.",
        },
      ],
    },
  },

  refund: {
    hr: {
      title: "Politika povrata",
      intro:
        "Ova Politika povrata odnosi se na pretplatu na LedgerLens uslugu. Napominjemo: „naknade“ unutar aplikacije (povrat PDV-a zaposlenicima) interna su funkcionalnost društva i nisu povezane s povratom pretplate.",
      sections: [
        { h: "1. Pružatelj usluge", list: identityBlock("hr") },
        {
          h: "2. Mjesečna pretplata i otkaz",
          list: [
            "Pretplata se naplaćuje mjesečno unaprijed i možete je otkazati u svakom trenutku.",
            "Otkaz stupa na snagu na kraju tekućeg obračunskog razdoblja — usluga ostaje dostupna do tada.",
            "Već plaćena mjesečna naknada ne vraća se proporcionalno za neiskorišteni dio razdoblja.",
          ],
        },
        {
          h: "3. Besplatni probni period",
          p: "Probni period od 14 dana omogućuje testiranje usluge bez plaćanja i bez kartice, pa se povrat u tom razdoblju ne primjenjuje.",
        },
        {
          h: "4. Prekoračenje broja skeniranja",
          p: "Skeniranja iznad uključenog broja naplaćuju se po jedinici i smatraju se iskorištenom uslugom te se ne vraćaju. Naplaćivanje prekoračenja možete isključiti u Postavkama, čime se skeniranje blokira na kvoti.",
        },
        {
          h: "5. Iznimke — kad odobravamo povrat",
          list: [
            "Dvostruka ili pogrešna naplata uzrokovana tehničkom greškom — puni povrat pogrešno naplaćenog iznosa.",
            "Neraspoloživost usluge duža od 72 sata unutar jednog obračunskog razdoblja koju je uzrokovao pružatelj — povrat proporcionalno danima nedostupnosti.",
            "Neovlaštena naplata nakon potvrđenog otkaza — puni povrat.",
          ],
        },
        {
          h: "6. Kako zatražiti povrat",
          p: `Zahtjev pošaljite na ${COMPANY.support} s nazivom društva, OIB-om, datumom naplate i opisom razloga. Na zahtjev odgovaramo u roku od 5 radnih dana; odobreni povrat izvršava se na izvorno sredstvo plaćanja u roku od 14 dana.`,
        },
        {
          h: "7. Potrošačka prava",
          p: "Usluga je namijenjena poslovnim subjektima (B2B), pa se zakonsko pravo potrošača na jednostrani raskid ugovora na daljinu ne primjenjuje. Ako uslugu koristi potrošač, primjenjuju se prava iz Zakona o zaštiti potrošača.",
        },
      ],
    },
    en: {
      title: "Refund Policy",
      intro:
        'This Refund Policy applies to the LedgerLens subscription. Note: "refunds" inside the app (VAT refunds to employees) are a company feature and are unrelated to subscription refunds.',
      sections: [
        { h: "1. Service provider", list: identityBlock("en") },
        {
          h: "2. Monthly subscription and cancellation",
          list: [
            "The subscription is billed monthly in advance and can be cancelled at any time.",
            "Cancellation takes effect at the end of the current billing period — the service stays available until then.",
            "Monthly fees already paid are not refunded pro rata for the unused part of the period.",
          ],
        },
        {
          h: "3. Free trial",
          p: "The 14-day trial lets you evaluate the service with no payment and no card, so refunds do not apply during that period.",
        },
        {
          h: "4. Scan overage",
          p: "Scans above the included volume are billed per scan, are considered consumed and are not refundable. You can switch billed overage off in Settings, which blocks scanning at the quota instead.",
        },
        {
          h: "5. Exceptions — when we do refund",
          list: [
            "Duplicate or incorrect charges caused by a technical error — full refund of the incorrectly charged amount.",
            "Service unavailability of more than 72 hours within one billing period caused by us — pro-rata refund for the days affected.",
            "A charge taken after a confirmed cancellation — full refund.",
          ],
        },
        {
          h: "6. How to request a refund",
          p: `Email ${COMPANY.support} with your company name, tax ID, the charge date and the reason. We respond within 5 business days; approved refunds are returned to the original payment method within 14 days.`,
        },
        {
          h: "7. Consumer rights",
          p: "The service is intended for business customers (B2B), so the statutory consumer right of withdrawal from distance contracts does not apply. Where the service is used by a consumer, rights under the Croatian Consumer Protection Act apply.",
        },
      ],
    },
  },
};

export default LEGAL;
